import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, TrendingUp, Globe, ShoppingBag, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Hero Section - Minimal and clean
const PageHero = () => {
  return (
    <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 bg-[#030303] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#D4AF37] rounded-full filter blur-[100px] sm:blur-[150px]" />
      </div>
      
      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p variants={fadeInUp} className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4">
            Referenciák
          </motion.p>
          <motion.h1 variants={fadeInUp} className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">
            Válogatott{" "}
            <span className="italic-accent text-gold-gradient">munkáim</span>
          </motion.h1>
          <motion.div variants={fadeInUp} className="gold-line mb-6 sm:mb-8" />
          <motion.p variants={fadeInUp} className="text-zinc-400 text-sm sm:text-base md:text-lg leading-relaxed">
            Ízelítő a portfóliómból. Minden itt látható projekt gondosan válogatott – 
            olyan munkák, amelyekre különösen büszke vagyok.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Stats Bar - Compact
const StatsBar = ({ websiteCount, webshopCount }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="grid grid-cols-3 gap-4 sm:gap-8 mb-10 sm:mb-16"
    >
      <div className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.02]">
        <div className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl text-[#D4AF37] mb-1">
          50+
        </div>
        <div className="text-zinc-500 text-[0.6rem] sm:text-xs tracking-wider uppercase">
          Projekt
        </div>
      </div>
      <div className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.02]">
        <div className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl text-[#D4AF37] mb-1">
          100%
        </div>
        <div className="text-zinc-500 text-[0.6rem] sm:text-xs tracking-wider uppercase">
          Sikeres
        </div>
      </div>
      <div className="text-center p-4 sm:p-6 border border-white/5 bg-white/[0.02]">
        <div className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl text-[#D4AF37] mb-1">
          5+
        </div>
        <div className="text-zinc-500 text-[0.6rem] sm:text-xs tracking-wider uppercase">
          Iparág
        </div>
      </div>
    </motion.div>
  );
};

// Category Section Header
const CategoryHeader = ({ icon: Icon, title, subtitle, count }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 sm:mb-12 md:mb-16 pb-4 border-b border-white/10"
  >
    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-0">
      <div className="w-10 h-10 sm:w-12 sm:h-12 border border-[#D4AF37]/30 flex items-center justify-center">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" strokeWidth={1.5} />
      </div>
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl text-white">
          {title}
        </h2>
        <p className="text-zinc-500 text-xs sm:text-sm">{subtitle}</p>
      </div>
    </div>
    <span className="text-[#D4AF37] text-xs sm:text-sm">{count} projekt</span>
  </motion.div>
);

// Project Grid - New card layout
const ProjectGrid = ({ references, clickable = true }) => {
  return (
    <motion.div 
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
    >
      {references.map((ref) => {
        const Wrapper = clickable && ref.url ? 'a' : 'div';
        const wrapperProps = clickable && ref.url 
          ? { href: ref.url, target: "_blank", rel: "noopener noreferrer" } 
          : {};

        return (
          <motion.div key={ref.id} variants={fadeInUp}>
            <Wrapper
              className={`group block ${clickable && ref.url ? 'cursor-pointer' : ''}`}
              {...wrapperProps}
              data-testid={`reference-card-${ref.id}`}
            >
              <div className="relative overflow-hidden bg-zinc-900 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500">
                {/* Image */}
                <div className="aspect-[16/10] relative overflow-hidden">
                  <img 
                    src={ref.image_url} 
                    alt={ref.title}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/20 to-transparent" />
                  
                  {/* Type Badge */}
                  <div className="absolute top-3 left-3 sm:top-4 sm:left-4">
                    <span className={`px-2 py-1 sm:px-3 sm:py-1 text-[0.5rem] sm:text-[0.6rem] uppercase tracking-wider ${
                      ref.project_type === 'webshop' 
                        ? 'bg-[#D4AF37] text-black' 
                        : ref.project_type === 'landing'
                        ? 'bg-white/20 text-[#D4AF37] backdrop-blur-sm'
                        : 'bg-white/10 text-white backdrop-blur-sm'
                    }`}>
                      {ref.project_type}
                    </span>
                  </div>

                  {/* External link icon */}
                  {clickable && ref.url && (
                    <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 flex items-center justify-center bg-black/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ArrowUpRight className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 sm:p-5">
                  <span className="text-[#D4AF37] text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.15em] mb-1 sm:mb-2 block">
                    {ref.industry}
                  </span>
                  <h3 className="font-['Cormorant_Garamond'] text-lg sm:text-xl text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {ref.title}
                  </h3>
                  <p className="text-zinc-500 text-xs sm:text-sm line-clamp-2 mb-3 sm:mb-4">
                    {ref.description}
                  </p>
                  
                  {/* Result */}
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" strokeWidth={1.5} />
                    <span className="text-[0.65rem] sm:text-xs">{ref.result}</span>
                  </div>
                </div>
              </div>
            </Wrapper>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 sm:w-[400px] h-64 sm:h-[400px] bg-[#D4AF37] rounded-full filter blur-[100px] sm:blur-[150px]" />
      </div>

      <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-3xl text-center relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-3 sm:mb-4"
          >
            Következő projekt
          </motion.p>
          
          <motion.h2 
            variants={fadeInUp}
            className="font-['Cormorant_Garamond'] text-2xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6"
          >
            A tiéd lehet a{" "}
            <span className="italic-accent text-gold-gradient">következő</span>
          </motion.h2>
          
          <motion.p 
            variants={fadeInUp}
            className="text-zinc-400 text-sm sm:text-base md:text-lg mb-8 sm:mb-10 max-w-xl mx-auto"
          >
            Beszéljük meg, hogyan segíthetek a vállalkozásod online sikerében.
          </motion.p>
          
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/kapcsolat">
              <Button className="btn-gold w-full sm:w-auto">
                Beszéljünk
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
            <Link to="/tervezo">
              <Button className="btn-gold-outline w-full sm:w-auto">
                Tervezd meg
                <ArrowUpRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Main References Page
export default function ReferencesPage() {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const response = await axios.get(`${API}/references`);
        setReferences(response.data);
      } catch (error) {
        console.error("Error fetching references:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReferences();
  }, []);

  const websites = references.filter(ref => ref.project_type === "weboldal");
  const landings = references.filter(ref => ref.project_type === "landing");
  const webshops = references.filter(ref => ref.project_type === "webshop");

  return (
    <div className="bg-[#030303]">
      <PageHero />

      <section className="py-8 sm:py-12 md:py-16" data-testid="references-section">
        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-7xl">
          <StatsBar websiteCount={websites.length} webshopCount={webshops.length} />
          
          {loading ? (
            <div className="space-y-16">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="aspect-[16/10] loading-shimmer rounded" />
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* Websites Section */}
              {websites.length > 0 && (
                <div className="mb-32 sm:mb-40 md:mb-48">
                  <CategoryHeader 
                    icon={Globe}
                    title="Weboldalak"
                    subtitle="Céges weboldalak"
                    count={websites.length}
                  />
                  <ProjectGrid references={websites} />
                </div>
              )}

              {/* Landing Pages Section */}
              {landings.length > 0 && (
                <div className="mb-32 sm:mb-40 md:mb-48">
                  <CategoryHeader 
                    icon={Layers}
                    title="Landing oldalak"
                    subtitle="Konverziófókuszú landing page-ek"
                    count={landings.length}
                  />
                  <ProjectGrid references={landings} />
                </div>
              )}

              {/* Webshops Section */}
              {webshops.length > 0 && (
                <div>
                  <CategoryHeader 
                    icon={ShoppingBag}
                    title="Webshopok"
                    subtitle="Tervezetek / Sablonok"
                    count={webshops.length}
                  />
                  <ProjectGrid references={webshops} clickable={false} />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
